import React from 'react';
import { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';
import { Box, Checkbox, FormControlLabel, MenuItem } from '@mui/material';
import { Controller, useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextQuestionParameters } from './question-parameters/TextQuestionParameters';
import { OptionsBasedQuestionParameters } from './question-parameters/OptionsBasedQuestionParameters';
import { NumberQuestionParameters } from './question-parameters/NumberQuestionParameters';
import { getQuestionTypeIcon } from '@/features/questionnaires/utils/questionTypeIcons';
import {
  DocumentQuestionParameters,
  VoiceQuestionParameters,
} from './question-parameters';
import { SelectInput, TextInput } from '@/shared';

export const QuestionHOC = ({ questionIndex }: { questionIndex: number }) => {
  const { t } = useTranslation();
  const form = useFormContext<QuestionnaireFormData>();
  const {
    control,
    formState: { isSubmitting },
  } = form;

  const questionType = useWatch({
    control,
    name: `questions.${questionIndex}.parameters.type`,
  });

  const renderParametersComponent = () => {
    switch (questionType) {
      case 'text':
        return <TextQuestionParameters questionIndex={questionIndex} />;
      case 'radio':
      case 'select':
      case 'checkboxes':
        return <OptionsBasedQuestionParameters questionIndex={questionIndex} />;
      case 'number':
        return <NumberQuestionParameters questionIndex={questionIndex} />;
      case 'document':
        return <DocumentQuestionParameters questionIndex={questionIndex} />;
      case 'voice':
        return <VoiceQuestionParameters questionIndex={questionIndex} />;
      default:
        return;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <SelectInput
        form={form}
        name={`questions.${questionIndex}.parameters.type`}
        label={t('pages.studies.questions.type')}
        onChange={() => {
          form.trigger(`questions`);
        }}
      >
        <MenuItem value="text">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getQuestionTypeIcon('text')}
            {t('pages.studies.questions.types.text')}
          </Box>
        </MenuItem>
        <MenuItem value="radio">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getQuestionTypeIcon('radio')}
            {t('pages.studies.questions.types.radio')}
          </Box>
        </MenuItem>
        <MenuItem value="select">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getQuestionTypeIcon('select')}
            {t('pages.studies.questions.types.select')}
          </Box>
        </MenuItem>
        <MenuItem value="checkboxes">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getQuestionTypeIcon('checkboxes')}
            {t('pages.studies.questions.types.checkboxes')}
          </Box>
        </MenuItem>
        <MenuItem value="date">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getQuestionTypeIcon('date')}
            {t('pages.studies.questions.types.date')}
          </Box>
        </MenuItem>
        <MenuItem value="number">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getQuestionTypeIcon('number')}
            {t('pages.studies.questions.types.number')}
          </Box>
        </MenuItem>
        <MenuItem value="document">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getQuestionTypeIcon('document')}
            {t('pages.studies.questions.types.document')}
          </Box>
        </MenuItem>
        <MenuItem value="voice">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getQuestionTypeIcon('voice')}
            {t('pages.studies.questions.types.voice')}
          </Box>
        </MenuItem>
      </SelectInput>

      <TextInput
        form={form}
        name={`questions.${questionIndex}.label`}
        label={t('pages.studies.questions.label')}
        sx={{ mt: 1 }}
        required
      />

      <TextInput
        form={form}
        name={`questions.${questionIndex}.internalName`}
        label={t('pages.studies.questions.internalName')}
        sx={{ mt: 1 }}
        required
      />

      <TextInput
        form={form}
        name={`questions.${questionIndex}.internalDescription`}
        label={t('pages.studies.questions.internalDescription')}
        sx={{ mt: 1 }}
        required
      />

      <TextInput
        form={form}
        name={`questions.${questionIndex}.description`}
        label={t('pages.studies.questions.description')}
        sx={{ mt: 1 }}
      />

      <Controller
        control={control}
        name={`questions.${questionIndex}.required`}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                {...field}
                checked={field.value}
                disabled={isSubmitting}
              />
            }
            label={t('pages.studies.questions.required')}
          />
        )}
      />

      {renderParametersComponent()}
    </Box>
  );
};
